import {
	OrderQuoteItemQuantityChangeSeverity,
	useUpdateOrderQuoteItemMutation,
	usePreviewOrderQuoteItemQuantityChangeQuery,
	type OrderQuoteItemQuantityInput,
	type PreviewOrderQuoteItemQuantityChangeQuery,
} from '@codezo/graphql';
import { AlertTriangle, Info, Plus, X } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';
import { cn } from '../../../lib/utils';
import { AnimatedHeight } from '../../ui/animated-height';
import { Button } from '../../ui/button';
import { DatePicker } from '../../ui/date-picker';
import {
	Dialog,
	DialogBody,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from '../../ui/dialog';
import { Label } from '../../ui/label';
import { NumberInput } from '../../ui/number-input';
import { QuantityChangeImpact, type EditedTier } from './QuantityChangeImpact';

export interface EditOrderQuoteItemValue {
	id: string;
	productName: string;
	/** Tiers as they are stored — the id is what keeps a tier's pricing attached. */
	quantities: Array<{ id: string; quantity: number }>;
	deadline: string | null;
}

/**
 * A tier row in the editor. `id` is the stored tier; dropping it from the payload
 * is what tells the backend to delete the tier and start the quantity over, so it
 * is carried separately from the value being typed.
 */
interface TierRow {
	key: string;
	id?: string;
	quantity: number;
	startOver: boolean;
}

type PreviewResult =
	PreviewOrderQuoteItemQuantityChangeQuery['previewOrderQuoteItemQuantityChange'];

interface EditOrderQuoteItemModalProps {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	item: EditOrderQuoteItemValue | null;
	onSuccess: () => void;
}

/** Send the id to keep the tier's priced work; omit it to start the tier over. */
function toQuantityInput(rows: TierRow[]): OrderQuoteItemQuantityInput[] {
	return rows
		.filter((row) => row.quantity > 0)
		.map((row) =>
			row.id && !row.startOver
				? { id: row.id, quantity: row.quantity }
				: { quantity: row.quantity }
		);
}

export function EditOrderQuoteItemModal({
	open,
	onOpenChange,
	item,
	onSuccess,
}: EditOrderQuoteItemModalProps) {
	const [rows, setRows] = useState<TierRow[]>([]);
	const [deadline, setDeadline] = useState<string | null>(null);
	const [origDeadline, setOrigDeadline] = useState<string | null>(null);
	const [step, setStep] = useState<'edit' | 'confirm'>('edit');
	// `no-cache` drops data to undefined the moment the variables change, which
	// would unmount the confirmation and collapse the dialog on every refetch.
	// Hold the last answer until the next one lands. Cleared per item, so
	// reopening the modal on a different product never shows its neighbour's.
	const [lastPreview, setLastPreview] = useState<PreviewResult | null>(null);
	const nextKey = useRef(0);

	// Dropping a quantity tier takes the offer pricing rows hanging off it with it,
	// so the offers query has to re-resolve alongside the quote.
	const [updateItem, { loading }] = useUpdateOrderQuoteItemMutation({
		refetchQueries: ['GetOrderQuoteOfferBatches'],
	});

	useEffect(() => {
		if (open && item) {
			setRows(
				item.quantities.map((q) => ({
					key: `tier-${nextKey.current++}`,
					id: q.id,
					quantity: q.quantity,
					startOver: false,
				}))
			);
			setDeadline(item.deadline);
			setOrigDeadline(item.deadline);
			setStep('edit');
			setLastPreview(null);
		}
	}, [open, item]);

	const quantities = useMemo(() => toQuantityInput(rows), [rows]);
	const original = useMemo(
		() => (item ? item.quantities.map((q) => ({ id: q.id, quantity: q.quantity })) : []),
		[item]
	);

	// The preview only reads, so it is safe to run on every pause in typing.
	const [debounced, setDebounced] = useState<OrderQuoteItemQuantityInput[]>([]);
	useEffect(() => {
		const timer = setTimeout(() => {
			// Hold the identity when nothing actually changed, so a toggle that
			// already pushed its payload through does not refetch a second time.
			setDebounced((prev) =>
				JSON.stringify(prev) === JSON.stringify(quantities) ? prev : quantities
			);
		}, 350);
		return () => clearTimeout(timer);
	}, [quantities]);

	const quantitiesChanged = JSON.stringify(quantities) !== JSON.stringify(original);
	const debouncedChanged = JSON.stringify(debounced) !== JSON.stringify(original);

	const {
		data: previewData,
		loading: previewLoading,
		error: previewError,
	} = usePreviewOrderQuoteItemQuantityChangeQuery({
		variables: { orderQuoteItemId: item?.id ?? '', quantities: debounced },
		skip: !open || !item || !debouncedChanged || debounced.length === 0,
		fetchPolicy: 'no-cache',
	});

	useEffect(() => {
		if (previewData?.previewOrderQuoteItemQuantityChange)
			setLastPreview(previewData.previewOrderQuoteItemQuantityChange);
	}, [previewData]);

	const preview =
		debouncedChanged && !previewError
			? (previewData?.previewOrderQuoteItemQuantityChange ?? lastPreview)
			: null;

	// While the debounce is still catching up, the preview on screen describes a
	// payload we are no longer about to send.
	const previewStale =
		quantitiesChanged &&
		!previewError &&
		(previewLoading || JSON.stringify(quantities) !== JSON.stringify(debounced));

	// Existing tiers whose value the operator changed — the ones the confirmation
	// asks a keep-or-restart question about.
	const editedTiers = useMemo<EditedTier[]>(() => {
		const originalById = new Map(original.map((q) => [q.id, q.quantity]));
		return rows.flatMap((row) => {
			if (!row.id) return [];
			const from = originalById.get(row.id);
			if (from === undefined || from === row.quantity) return [];
			return [
				{
					quantityId: row.id,
					fromQuantity: from,
					toQuantity: row.quantity,
					startOver: row.startOver,
				},
			];
		});
	}, [rows, original]);

	const toggleStartOver = (quantityId: string) => {
		const next = rows.map((row) =>
			row.id === quantityId ? { ...row, startOver: !row.startOver } : row
		);
		setRows(next);
		// Typing needs a debounce; a click on the choice does not.
		setDebounced(toQuantityInput(next));
	};

	const hasChanges = quantitiesChanged || deadline !== origDeadline;
	const blockedByPreview = !!preview && !preview.accepted;
	const needsConfirmation =
		!!preview &&
		preview.accepted &&
		preview.severity !== OrderQuoteItemQuantityChangeSeverity.None;

	const save = async () => {
		if (!item) return;
		try {
			const { data: res } = await updateItem({
				variables: {
					id: item.id,
					input: {
						quantities,
						// Send null (not undefined) so clearing the deadline persists.
						deadline: deadline,
					},
				},
			});
			if (res?.updateOrderQuoteItem?.success) {
				toast.success('Item updated');
				onSuccess();
				onOpenChange(false);
			} else {
				toast.error(res?.updateOrderQuoteItem?.message || 'Failed to update item');
			}
		} catch {
			toast.error('Failed to update item');
		}
	};

	const handlePrimary = () => {
		// Severity decides whether this is routine — what a tier costs depends on
		// what is priced underneath it, which only the backend can see.
		if (step === 'edit' && needsConfirmation) {
			setStep('confirm');
			return;
		}
		void save();
	};

	const primaryDisabled =
		loading ||
		!hasChanges ||
		rows.every((row) => row.quantity <= 0) ||
		blockedByPreview ||
		previewStale;

	const isDestructive = preview?.severity === OrderQuoteItemQuantityChangeSeverity.Destructive;

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent className="sm:max-w-md">
				<DialogHeader>
					<DialogTitle>
						{step === 'confirm' ? 'Confirm changes' : 'Edit item'}
					</DialogTitle>
					<DialogDescription>{item?.productName}</DialogDescription>
				</DialogHeader>
				{item && (
					<>
						<DialogBody>
							<AnimatedHeight
								step={`${step}:${rows.length}:${preview?.changes.length ?? 0}:${editedTiers
									.map((t) => `${t.quantityId}${t.startOver}`)
									.join(',')}`}
							>
								{step === 'confirm' ? (
									<div className="flex flex-col gap-2">
										{editedTiers.length > 0 && (
											<p className="text-xs leading-relaxed text-text-3">
												Choose what happens to each quantity you changed.
											</p>
										)}
										<QuantityChangeImpact
											preview={preview}
											editedTiers={editedTiers}
											onToggleStartOver={toggleStartOver}
											stale={previewStale}
										/>
									</div>
								) : (
									<>
										<div className="flex flex-col gap-1.5">
											{rows.map((row, i) => (
												<div
													key={row.key}
													className="flex items-center gap-1.5"
												>
													<NumberInput
														value={row.quantity || ''}
														onChange={(e) => {
															const val =
																parseInt(e.target.value) || 0;
															setRows((prev) =>
																prev.map((r, idx) =>
																	idx === i
																		? { ...r, quantity: val }
																		: r
																)
															);
														}}
														min={1}
														placeholder="Quantity"
														className="flex-1"
													/>
													{rows.length > 1 && (
														<button
															type="button"
															onClick={() =>
																setRows((prev) =>
																	prev.filter(
																		(_, idx) => idx !== i
																	)
																)
															}
															className="flex h-8 w-8 shrink-0 cursor-pointer items-center justify-center rounded-lg text-text-4 transition-colors hover:bg-surface-hover hover:text-text-2"
														>
															<X size={14} />
														</button>
													)}
												</div>
											))}
										</div>
										<button
											type="button"
											onClick={() => {
												if (rows.length < 3)
													setRows((prev) => [
														...prev,
														{
															key: `tier-${nextKey.current++}`,
															quantity: 0,
															startOver: false,
														},
													]);
											}}
											disabled={rows.length >= 3}
											className="mt-2 flex items-center gap-1 self-start bg-transparent text-sm text-text-3 transition-colors enabled:hover:text-text-1 disabled:cursor-not-allowed disabled:opacity-40"
										>
											<Plus size={12} />
											{rows.length >= 3 ? 'Max 3 quantities' : 'Add quantity'}
										</button>
										{/* Refusals (a duplicate quantity, a tier someone else removed)
										    are worth showing before the operator hits Save. */}
										{blockedByPreview && (
											<div className="mt-3">
												<QuantityChangeImpact
													preview={preview}
													editedTiers={editedTiers}
													onToggleStartOver={toggleStartOver}
												/>
											</div>
										)}
										{/* Say what is at stake while they are still typing, rather
										    than only once they reach for Save. */}
										{!blockedByPreview && needsConfirmation && (
											<div
												className={cn(
													'mt-3 flex items-start gap-1.5 rounded-lg border px-2.5 py-2',
													isDestructive
														? 'border-status-danger/25 bg-status-danger/5'
														: 'border-status-warn/25 bg-status-warn/5'
												)}
											>
												{isDestructive ? (
													<AlertTriangle
														size={14}
														className="mt-1 shrink-0 text-status-danger"
													/>
												) : (
													<Info
														size={14}
														className="mt-1 shrink-0 text-status-warn"
													/>
												)}
												<span className="text-xs leading-relaxed text-text-2">
													{isDestructive
														? 'This edit deletes priced work.'
														: 'These quantities already have work priced against them.'}{' '}
													Review before saving.
												</span>
											</div>
										)}
										<div className="mt-4 flex flex-col gap-1.5">
											<Label>Deadline</Label>
											<DatePicker
												value={deadline ? new Date(deadline) : null}
												onChange={(d: Date | undefined) =>
													setDeadline(d?.toISOString() ?? null)
												}
												minDate={new Date()}
												placeholder="No deadline"
												clearable
											/>
										</div>
									</>
								)}
							</AnimatedHeight>
						</DialogBody>
						<DialogFooter>
							<Button
								variant="outline"
								onClick={() =>
									step === 'confirm' ? setStep('edit') : onOpenChange(false)
								}
							>
								{step === 'confirm' ? 'Back' : 'Cancel'}
							</Button>
							<Button
								variant={
									step === 'confirm' && isDestructive ? 'destructive' : 'default'
								}
								disabled={primaryDisabled}
								onClick={handlePrimary}
							>
								{loading
									? 'Saving…'
									: previewStale
										? 'Checking…'
										: step === 'confirm'
											? isDestructive
												? 'Save anyway'
												: 'Save changes'
											: needsConfirmation
												? 'Review changes'
												: 'Save'}
							</Button>
						</DialogFooter>
					</>
				)}
			</DialogContent>
		</Dialog>
	);
}
